import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

export function ProtectedRoute({
  children,
  requireSuperuser = false,
  requireAdmin = false,
}: {
  children: React.ReactNode;
  requireSuperuser?: boolean;
  requireAdmin?: boolean;
}) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // 1. Check if authentication status is still loading
  if (isLoading) {
    // Render a loading indicator while checking auth status
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="ml-4 text-muted-foreground">Loading...</p>
        <span className="loading loading-ring loading-xl"></span>
      </div>
    );
  }

  // 2. If not loading, check if authenticated
  if (!isAuthenticated) {
    // Redirect to login if not authenticated, preserving the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3. If authenticated, check permissions
  // Rule A: If user is a superuser, grant access regardless of other props.
  if (user && user.superuser) {
    return <>{children}</>;
  }

  // User is NOT a superuser. Evaluate props for non-superusers.
  if (requireAdmin && requireSuperuser) {
    // Case: Both admin and superuser props are set (e.g., UserManagementPage).
    // A non-superuser needs to have the 'admin' role.
    if (user && user.roles?.includes("admin")) {
      return <>{children}</>;
    } else {
      // Not an admin, deny access.
      return <Navigate to="/" replace />;
    }
  } else if (requireAdmin) {
    // Case: Only admin prop is set.
    // A non-superuser needs to have the 'admin' role.
    if (user && user.roles?.includes("admin")) {
      return <>{children}</>;
    } else {
      // Not an admin, deny access.
      return <Navigate to="/" replace />;
    }
  } else if (requireSuperuser) {
    // Case: Only superuser prop is set.
    // Since the user is not a superuser (checked above), deny access.
    // This route is exclusively for superusers.
    return <Navigate to="/" replace />;
  }

  // Case: Neither requireAdmin nor requireSuperuser is true,
  // or user met the conditions in one of the blocks above.
  // Grant access by default if no specific requirement denied it.
  return <>{children}</>;
}
